import { SLICE_ANGLE, TOTAL_SLICES } from "./constants";
import { RADIAL_BANDS } from "./radialBands";
import { describeWedgePath } from "./geometry";

export function generateRegions() {
  const regions = [];
  for (let sliceIndex = 0; sliceIndex < TOTAL_SLICES; sliceIndex++) {
    const startAngle = sliceIndex * SLICE_ANGLE;
    const endAngle = (sliceIndex + 1) * SLICE_ANGLE;
    for (const band of RADIAL_BANDS) {
      regions.push({
        regionId: `${sliceIndex}-${band.bandIndex}`,
        sliceIndex,
        bandIndex: band.bandIndex,
        startAngle,
        endAngle,
        innerRadius: band.innerRadius,
        outerRadius: band.outerRadius,
        path: describeWedgePath(
          startAngle,
          endAngle,
          band.innerRadius,
          band.outerRadius,
        ),
      });
    }
  }
  return regions;
}

// Universal 8-region wagon wheel — Vikram's feedback (2026-05-07).
// Single full-radius wedge per universal cricketing position. RH-batter
// reference; LH is mirrored at render time by swapping label index.
//
// Sector mapping (45° each, slice 0 = straight toward bowler / 6 o'clock,
// angle increases clockwise):
//   s=0 (slices 0-5,   0°-45°)   → Long Off
//   s=1 (slices 6-11,  45°-90°)  → Covers
//   s=2 (slices 12-17, 90°-135°) → Point
//   s=3 (slices 18-23, 135°-180°)→ Third Man
//   s=4 (slices 24-29, 180°-225°)→ Fine Leg
//   s=5 (slices 30-35, 225°-270°)→ Square Leg
//   s=6 (slices 36-41, 270°-315°)→ Mid Wicket
//   s=7 (slices 42-47, 315°-360°)→ Long On
const UNIVERSAL_LABELS_RH = [
  "Long Off", "Covers", "Point", "Third Man",
  "Fine Leg", "Square Leg", "Mid Wicket", "Long On",
];

export function universalRegionLabel(sectorIdx, batterHand = "RIGHT") {
  if (sectorIdx < 0 || sectorIdx > 7) return "Unknown";
  // For LH batter, off and leg sides flip — mirror across the straight line.
  // s=0 (Long Off) <-> s=7 (Long On), s=1 <-> s=6, s=2 <-> s=5, s=3 <-> s=4.
  const idx = batterHand === "LEFT" ? 7 - sectorIdx : sectorIdx;
  return UNIVERSAL_LABELS_RH[idx];
}

const SECTOR_SLICE_WIDTH = 6;        // 48 / 8

export function generate8Regions() {
  const regions = [];
  for (let s = 0; s < 8; s++) {
    const startAngle = s * SECTOR_SLICE_WIDTH * SLICE_ANGLE;
    const endAngle = (s + 1) * SECTOR_SLICE_WIDTH * SLICE_ANGLE;
    const repSlice = s * SECTOR_SLICE_WIDTH + Math.floor(SECTOR_SLICE_WIDTH / 2); // 3, 9, 15…
    // Use mid-band (3) as the representative band so legacy decoders that
    // look up POSITION_MAP[band][slice] still get a sensible answer.
    const repBand = 3;
    const innerRadius = 0;
    const outerRadius = RADIAL_BANDS[RADIAL_BANDS.length - 1].outerRadius;
    regions.push({
      regionId: `${repSlice}-${repBand}`,
      sliceIndex: repSlice,
      bandIndex: repBand,
      sectorIndex: s,
      startAngle,
      endAngle,
      innerRadius,
      outerRadius,
      bandKind: "full",
      path: describeWedgePath(startAngle, endAngle, innerRadius, outerRadius),
    });
  }
  return regions;
}

// Coarse 8-sector × 2-band grid (16 zones) for the live-scoring INPUT UI.
// Two bands match how scorers naturally talk about balls — "went to the ring"
// (inside the 30-yard circle) vs "to the deep / boundary" (outside it).
//
// Each macro zone encodes a representative (sliceIndex, bandIndex) from the
// underlying 48×6 grid so the stored regionId stays format-compatible with
// the fine grid. Analytics and historical data keep working unchanged.
export const COARSE_SECTORS = 8;
export const COARSE_BANDS = 2;
const SLICES_PER_SECTOR = TOTAL_SLICES / COARSE_SECTORS;       // 48 / 8 = 6
const BANDS_PER_COARSE_BAND = RADIAL_BANDS.length / COARSE_BANDS; // 6 / 2 = 3

export function generateCoarseRegions() {
  // 16-zone layout: 8 sectors × {ring, deep} = 16. Numbered 1–16 in
  // visual order — for each sector going clockwise from sector 0
  // (Long Off), ring (inner) first, then deep (outer). So the
  // operator reads the badge off the wheel and voices it.
  //   1 = sector 0 ring (Mid Off / "Mid Off") · 2 = sector 0 deep
  //       (Long Off)
  //   3 = sector 1 ring (Cover) · 4 = sector 1 deep (Deep Cover)
  //   ...
  //   15 = sector 7 ring (Mid On) · 16 = sector 7 deep (Long On)
  // Founder ask 2026-05-27 — same "say the number" UX as pitch /
  // impact, but with 16 zones for finer granularity than the
  // sector-only 1-8 used previously.
  const regions = [];
  let cellNumber = 0;
  for (let s = 0; s < COARSE_SECTORS; s++) {
    const startAngle = s * SLICES_PER_SECTOR * SLICE_ANGLE;
    const endAngle = (s + 1) * SLICES_PER_SECTOR * SLICE_ANGLE;
    // Representative slice = middle of the sector (e.g. s=0 → slice 3)
    const repSlice = s * SLICES_PER_SECTOR + Math.floor(SLICES_PER_SECTOR / 2);

    for (let b = 0; b < COARSE_BANDS; b++) {
      const innerBand = RADIAL_BANDS[b * BANDS_PER_COARSE_BAND];
      const outerBand = RADIAL_BANDS[b * BANDS_PER_COARSE_BAND + BANDS_PER_COARSE_BAND - 1];
      // Representative band: ring=2 (Inner Ring), deep=5 (Boundary).
      // Picked so resolvePosition() returns the right canonical label
      // for that zone (Mid Off vs Long Off, Cover vs Deep Cover, etc.).
      const repBand = outerBand.bandIndex;
      cellNumber += 1;

      regions.push({
        regionId: `${repSlice}-${repBand}`,
        sliceIndex: repSlice,
        bandIndex: repBand,
        sectorIndex: s,
        cellNumber,
        startAngle,
        endAngle,
        innerRadius: innerBand.innerRadius,
        outerRadius: outerBand.outerRadius,
        bandKind: b === 0 ? "ring" : "deep",
        path: describeWedgePath(
          startAngle,
          endAngle,
          innerBand.innerRadius,
          outerBand.outerRadius,
        ),
      });
    }
  }
  return regions;
}

// Map a voice-spoken cell number (1-16) to its (sliceIndex, bandIndex).
// Exported so useVoiceScoring can resolve "wagon 5" without recomputing
// the geometry. Returns null for out-of-range input.
export function wagonCellNumberToSliceBand(n) {
  if (n < 1 || n > 16) return null;
  const sectorIdx = Math.floor((n - 1) / 2);
  const isDeep = (n - 1) % 2 === 1;
  const sliceIndex = sectorIdx * SLICES_PER_SECTOR + Math.floor(SLICES_PER_SECTOR / 2);
  // Inner ring → band 2, deep → band 5. Mirrors generateCoarseRegions
  // so the chosen cell highlights cleanly.
  const bandIndex = isDeep ? 5 : 2;
  return { sliceIndex, bandIndex, sectorIndex: sectorIdx, isDeep };
}
